import { useEffect, useMemo, useRef } from 'react';
import { bandColor } from '../lib/bands.js';

export default function Table({
  spots, alertIds,
  selectedId, onSelect,
  hoverId, onHover,
}) {
  const rows = useMemo(() => spots, [spots]);
  const seenRef = useRef(new Set());
  const flashRef = useRef(new Set());

  useEffect(() => {
    const newIds = [];
    for (const s of spots) {
      if (!seenRef.current.has(s.id)) {
        seenRef.current.add(s.id);
        newIds.push(s.id);
      }
    }
    if (newIds.length === 0) return;
    for (const id of newIds) flashRef.current.add(id);
    const t = setTimeout(() => {
      for (const id of newIds) flashRef.current.delete(id);
    }, 1500);
    return () => clearTimeout(t);
  }, [spots]);

  const onContext = (s) => (e) => {
    e.preventDefault();
    window.open(`https://www.qrz.com/db/${encodeURIComponent(s.dx_callsign)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="overflow-auto h-full text-xs sm:text-[13px] font-mono">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-16" />  {/* UTC HH:MM */}
          <col className="w-20" />  {/* Freq */}
          <col className="w-7" />   {/* B chip */}
          <col className="w-12" />  {/* Mode */}
          <col className="w-24" />                      {/* DX */}
          <col className="w-24 lg:w-28 2xl:w-32" />     {/* Country (capped — Comment is privileged) */}
          <col />                                       {/* Comment (flex; absorbs remaining width) */}
          <col className="w-20" />                      {/* de (spotter) */}
        </colgroup>
        <thead className="sticky top-0 bg-slate-900 text-slate-300 text-[10px] uppercase tracking-wide z-10">
          <tr>
            <th className="text-left px-2 py-2 font-medium">UTC</th>
            <th className="text-right px-2 py-2 font-medium">Freq</th>
            <th className="text-left px-1 py-2 font-medium">B</th>
            <th className="text-left px-2 py-2 font-medium">Mode</th>
            <th className="text-left px-2 py-2 font-medium">DX</th>
            <th className="text-left px-2 py-2 font-medium">Country</th>
            <th className="text-left px-2 py-2 font-medium">Comment</th>
            <th className="text-left px-2 py-2 font-medium">de</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const sel = s.id === selectedId;
            const hov = s.id === hoverId;
            const alert = alertIds.has(s.id);
            const flash = flashRef.current.has(s.id);
            const baseBg = sel ? 'bg-slate-700/70'
                         : hov ? 'bg-slate-800/80'
                         : alert ? 'bg-amber-500/10'
                         : '';
            return (
              <tr key={s.id}
                  onClick={() => onSelect(s.id === selectedId ? null : s.id)}
                  onMouseEnter={() => onHover(s.id)}
                  onMouseLeave={() => onHover(null)}
                  onContextMenu={onContext(s)}
                  className={`border-b border-slate-800 cursor-pointer hover:bg-slate-800/60 ${baseBg} ${flash ? 'spot-flash' : ''}`}>
                <td className="px-2 py-1 text-slate-400 truncate">{s.time?.slice(11, 16) || ''}</td>
                <td className="px-2 py-1 text-right text-slate-200 tabular-nums truncate">{s.frequency.toFixed(1)}</td>
                <td className="px-1 py-1">
                  <span className="inline-block w-3 h-3 rounded-sm align-middle"
                        style={{ background: bandColor(s.band) }} />
                </td>
                <td className="px-2 py-1 text-slate-300 truncate">{s.mode}</td>
                <td className={`px-2 py-1 font-semibold truncate ${alert ? 'text-amber-300' : ''}`} title={s.dx_callsign}>
                  {s.dx_callsign}
                </td>
                <td className="px-2 py-1 text-slate-300 truncate" title={`${s.dx_country} (${s.dx_continent})`}>
                  {s.dx_country}
                </td>
                <td className="px-2 py-1 text-slate-400 truncate" title={s.comment}>
                  {s.comment}
                </td>
                <td className="px-2 py-1 text-slate-500 truncate" title={s.spotter_callsign}>
                  {s.spotter_callsign}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
